import { GetColab } from '../../modules/Colab/types';

type ColabModelQuickFilterProps = {
  records: GetColab[];
  selectedModel: string;
  onChange: (model: string) => void;
};

type ModelTab = {
  count: number;
  current: boolean;
  id: string;
  label: string;
  value: string;
};

export const allModelQuickFilter = 'All';
const unknownModelQuickFilter = '__unknown__';

export function getCarModelLabel(value: string | undefined) {
  const model = (value || '').trim();

  if (!model || model === unknownModelQuickFilter) {
    return 'Unknown';
  }

  if (model === 'CX11') {
    return 'CX11 A1';
  }

  return model.replace(/_/g, ' ');
}

export function getCarModelQuickFilterValue(record: GetColab) {
  return (record.vehicleModel || '').trim() || unknownModelQuickFilter;
}

function getModelTabs(records: GetColab[], selectedModel: string): ModelTab[] {
  const counts = records.reduce((acc, record) => {
    const model = getCarModelQuickFilterValue(record);
    acc.set(model, (acc.get(model) || 0) + 1);
    return acc;
  }, new Map<string, number>());

  const modelTabs = Array.from(counts.entries())
    .sort(([firstModel], [secondModel]) => getCarModelLabel(firstModel).localeCompare(getCarModelLabel(secondModel)))
    .map(([model, count], index) => ({
      count,
      current: selectedModel === model,
      id: `${model}-${index}`,
      label: getCarModelLabel(model),
      value: model,
    }));

  return [
    {
      count: records.length,
      current: selectedModel === allModelQuickFilter,
      id: allModelQuickFilter,
      label: allModelQuickFilter,
      value: allModelQuickFilter,
    },
    ...modelTabs,
  ];
}

function classNames(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

function ColabModelQuickFilter({ records, selectedModel, onChange }: ColabModelQuickFilterProps) {
  const tabs = getModelTabs(records, selectedModel);

  return (
    <div className="border-y border-gray-200 bg-white">
      <div className="overflow-x-auto px-6">
        <nav className="-mb-px flex space-x-6 md:space-x-12" aria-label="Car model filters">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.value)}
              className={classNames(
                tab.current
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700',
                'flex cursor-pointer items-center whitespace-nowrap border-b py-4 text-base',
              )}
              aria-current={tab.current ? 'page' : undefined}>
              {tab.label}
              <span
                className={classNames(
                  tab.current ? 'border-primary-500 bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-900',
                  'ml-3 hidden rounded-full py-0.5 px-2.5 text-xs md:inline-block',
                )}>
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}

export default ColabModelQuickFilter;
